import React from "react";
import {
  CheckCircle2,
  FileText,
  UserPlus,
  GraduationCap,
  AlertCircle,
  Clock,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { NotificationType } from "@/services/notification.service";
import { formatTimeAgo } from "@/utils/formatDate.util";
import { Badge } from "@/components/ui/badge";

interface NotificationItemProps {
  notification: NotificationType;
  onMarkRead?: (id: string) => void;
}

const TYPE_CONFIG = {
  placement_confirmed: {
    icon: CheckCircle2,
    color: "text-green-500",
    bgColor: "bg-green-50",
  },
  internship_application: {
    icon: FileText,
    color: "text-blue-500",
    bgColor: "bg-blue-50",
  },
  vendor_review: {
    icon: Clock,
    color: "text-orange-500",
    bgColor: "bg-orange-50",
  },
  student_enrollment: {
    icon: UserPlus,
    color: "text-blue-600",
    bgColor: "bg-blue-100/50",
  },
  program_completed: {
    icon: GraduationCap,
    color: "text-green-600",
    bgColor: "bg-green-100/50",
  },
};

const NotificationItem: React.FC<NotificationItemProps> = ({
  notification,
  onMarkRead,
}) => {
  const config = TYPE_CONFIG[notification.type] || {
    icon: AlertCircle,
    color: "text-gray-500",
    bgColor: "bg-gray-50",
  };

  return (
    <div
      className={cn(
        "relative flex gap-4 border-l-4 p-6 transition-all duration-200",
        notification.isRead
          ? "border-transparent bg-white hover:bg-gray-50/50"
          : "border-blue-500 bg-blue-50/30 shadow-sm"
      )}
      onClick={() => !notification.isRead && onMarkRead?.(notification.id)}
    >
      <div className={cn("h-fit shrink-0 rounded-full p-2.5", config.bgColor)}>
        <config.icon className={cn("h-5 w-5", config.color)} />
      </div>

      <div className="flex-1 space-y-1">
        <div className="flex items-start justify-between gap-4">
          <h3 className="font-bold leading-tight text-gray-900">
            {notification.title}
          </h3>
          {!notification.isRead && (
            <Badge className="h-5 shrink-0 rounded-sm bg-black px-2 py-0 text-[10px] font-bold uppercase tracking-wider text-white">
              New
            </Badge>
          )}
        </div>

        <p className="text-sm font-medium leading-relaxed text-gray-600">
          {notification.description}
        </p>

        <p className="pt-1 text-[11px] font-bold uppercase tracking-widest text-gray-400">
          {formatTimeAgo(notification.timestamp)}
        </p>
      </div>
    </div>
  );
};

export default NotificationItem;
